
import React, { useEffect } from "react";
import {
  Button,
  Col,
  Icon,
  Row,
  RSelect,
} from "../../components/Component";

import { ErrorMessage } from "@hookform/error-message";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  Input,
  Modal,
  ModalBody,
} from "reactstrap";
import { GET, POST, PUT } from "../../helper/api";
import { CUSTOMER_LOCATION, defaultPNCustomer, getCustomerLocationOptions } from '../../utils/Utils';
import RTag from "../../components/tags/ReactTag";

const PNUpsertCustomer = ({ type, setLoading, onSuccess, onFormCancel, isOpen, editId }) => {
  const normalLabel = type === 'OUT' ? "khách hàng" : "nhà cung cấp";
  const locationOptions = getCustomerLocationOptions(type);
  const { errors, register, handleSubmit, reset, getValues, control, setValue } = useForm({
    defaultValues: defaultPNCustomer(),
  });

  const findInfoByTaxCode = async () => {
    const taxCode = getValues('metadata.taxCode');
    if (!taxCode) return;
    const { data: { data, message } } = await GET(`/common/find-info-by-tax-code/${taxCode}`, {}, true);
    if (!data) {
      toast.error(message || 'Không tìm thấy thông tin');
      return;
    }
    setValue('metadata.taxCompanyName', data.name);
    setValue('metadata.taxAddress', data.address);
    setValue('metadata.address', data.address);
  };

  useEffect(() => {
    if (!editId) return;
    setLoading(true);
    GET(`/pn-customer/${editId}`, {}, true)
      .then(({ data }) => {
        reset({ ...data, location: data.location || CUSTOMER_LOCATION.INTERNAL });
      })
      .catch(({ response }) => {
        toast.error(response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại.");
      })
      .finally(() => setLoading(false));
  }, [editId]);

  const onHandleSubmit = () => {
    setLoading(true);
    const { name, id, isNeedClaimCustomer, metadata, location } = getValues();
    const formData = { id, name, type, location: location || CUSTOMER_LOCATION.INTERNAL, isNeedClaimCustomer, metadata };
    const request = editId
      ? PUT(`/pn-customer/${editId}`, formData, {}, true)
      : POST(`/pn-customer`, formData, {}, true);

    request
      .then((res) => {
        toast.success(editId ? "Sửa thành công" : "Thêm thành công");
        onSuccess(res.data);
      })
      .catch(({ response }) => {
        toast.error(response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại.");
      })
      .finally(() => setLoading(false));
  };

  const onCancel = () => {
    reset({ ...defaultPNCustomer() });
    onFormCancel();
  };

  return (
    <Modal isOpen={isOpen} toggle={onCancel} className="modal-dialog-centered" size="xl">
      <ModalBody>
        <a href="#cancel" className="close">
          <Icon
            name="cross-sm"
            onClick={(ev) => {
              ev.preventDefault();
              onCancel();
            }}
          />
        </a>
        <div className="p-2">
          <h5 className="title">{editId ? 'Sửa' : 'Thêm'} {normalLabel}</h5>
          <div className="mt-4">
            <form onSubmit={handleSubmit(onHandleSubmit)} className="g-3">
              <Row className="g-3">
                <Col md="6">
                  <div className="form-group">
                    <label className="form-label">
                      Mã {normalLabel} <span className="text-danger">*</span>
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        className="form-control"
                        name="id"
                        ref={register({ required: "Vui lòng nhập thông tin" })}
                        disabled={!!editId}
                      />
                      <ErrorMessage
                        errors={errors}
                        name="id"
                        render={({ message }) => <span className="invalid">{message}</span>}
                      />
                    </div>
                  </div>
                </Col>

                <Col md="6">
                  <div className="form-group">
                    <label className="form-label">
                      Tên {normalLabel} <span className="text-danger">*</span>
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        ref={register({ required: "Vui lòng nhập thông tin" })}
                      />
                      <ErrorMessage
                        errors={errors}
                        name="name"
                        render={({ message }) => <span className="invalid">{message}</span>}
                      />
                    </div>
                  </div>
                </Col>

                <Col md="12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="location">
                      Location
                    </label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        name="location"
                        render={({ onChange, value, name, ref }) => (
                          <RSelect
                            inputRef={ref}
                            name={name}
                            options={locationOptions}
                            value={locationOptions.find((c) => c.value === (value || CUSTOMER_LOCATION.INTERNAL))}
                            onChange={(val) => onChange(val.value)}
                          />
                        )}
                      />
                    </div>
                  </div>
                </Col>
                <Col md="12" className="d-none">
                  <div className="form-group">
                    <label className="form-label">Thu công nợ TL và CT</label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        name="isNeedClaimCustomer"
                        render={({ onChange, value, ref }) => (
                          <div className="custom-control custom-switch">
                            <Input
                              id="isNeedClaimCustomer"
                              className="custom-control-input"
                              inputRef={ref}
                              type="checkbox"
                              checked={!!value}
                              onClick={() => onChange(!value)}
                            />
                            <label className="custom-control-label" htmlFor="isNeedClaimCustomer" />
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </Col>
              </Row>

              <hr />

              <Row className="g-3">
                <Col md="4">
                  <div className="form-group">
                    <label className="form-label">Mã số thuế</label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        name="metadata.taxCode"
                        render={(field) => (
                          <div className="d-flex">
                            <input
                              type="text"
                              className="form-control"
                              {...field}
                              onChange={(e) => field.onChange(e.target.value.replace(/[^0-9\-]/g, ''))}
                            />
                            <Button className="btn btn-secondary" onClick={findInfoByTaxCode} type="button">
                              <Icon name="search" />
                            </Button>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </Col>

                <Col md="4">
                  <div className="form-group">
                    <label className="form-label">Tên công ty</label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        name="metadata.taxCompanyName"
                        render={(field) => (
                          <input
                            type="text"
                            className="form-control"
                            {...field}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                        )}
                      />
                    </div>
                  </div>
                </Col>

                <Col md="4">
                  <div className="form-group">
                    <label className="form-label">Số điện thoại</label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        name="metadata.phoneNumber"
                        render={(field) => (
                          <input
                            type="text"
                            className="form-control"
                            {...field}
                            onChange={(e) => field.onChange(e.target.value.replace(/[^0-9]/g, ''))}
                          />
                        )}
                      />
                    </div>
                  </div>
                </Col>

                <Col md="12">
                  <div className="form-group">
                    <label className="form-label">Địa chỉ thuế công ty</label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        name="metadata.taxAddress"
                        render={(field) => (
                          <input
                            type="text"
                            className="form-control"
                            {...field}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                        )}
                      />
                    </div>
                  </div>
                </Col>

                <Col md="12">
                  <div className="form-group">
                    <label className="form-label">Email xuất VAT</label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        name="metadata.vatEmail"
                        render={({ onChange, value }) => {
                          const tags = value
                            ? value.split(',').map((e) => e.trim()).filter(Boolean).map((e) => ({ id: e, text: e }))
                            : [];
                          return (
                            <RTag
                              tags={tags}
                              handleDelete={(i) => onChange(tags.filter((_, idx) => idx !== i).map((t) => t.text).join(','))}
                              handleAddition={(tag) => onChange([...tags, tag].map((t) => t.text).join(','))}
                              delimiters={[188, 186, 13]}
                              placeholder="Nhập email rồi Enter hoặc dấu phẩy"
                              allowUnique
                            />
                          );
                        }}
                      />
                    </div>
                  </div>
                </Col>

                <Col md="12">
                  <div className="form-group">
                    <label className="form-label">Địa chỉ công ty</label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        name="metadata.address"
                        render={(field) => (
                          <input
                            type="text"
                            className="form-control"
                            {...field}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                        )}
                      />
                    </div>
                  </div>
                </Col>

                <Col md="12">
                  <div className="form-group">
                    <label className="form-label">Ghi chú</label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        name="metadata.note"
                        render={(field) => (
                          <textarea
                            className="form-control"
                            style={{ minHeight: "50px" }}
                            {...field}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                        )}
                      />
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col size="12">
                  <Button color="primary" type="submit">
                    <Icon className="plus" />
                    <span>{editId ? 'Sửa' : 'Thêm'} {normalLabel}</span>
                  </Button>
                </Col>
              </Row>
            </form>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default PNUpsertCustomer;
